import { createContext, useContext } from 'react';
import type { Context } from 'react';

export * from './assertions';
export * from './events';
export * from './vec2';
export * from './persistence';
export * from './widgetData';
export * from './numUtil';
export * from './atom';
export * from './2DUtil';
export * from './matrix';
export * from './clickOutside';

export type Mutable<T> = { -readonly [P in keyof T]: T[P] };

/** Limit numeric value to specified `[min;max]` range. */
export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(min, value), max);
}

/**  linear interpolation between two values */
export function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

/** qubic polynomial smooth minimum of two values with given knee size */
export function smoothmin(a: number, b: number, k: number) {
  const h = Math.max(k - Math.abs(a - b), 0.0) / k;
  return Math.min(a, b) - h * h * h * k * (1.0 / 6.0);
}

/** Map value from linear range `[start1; end1]` to linear range `[start2; end2]`. */
export function mapRange(
  value: number,
  start1: number,
  end1: number,
  start2: number,
  end2: number
): number {
  const p = (value - start1) / (end1 - start1);
  return start2 + p * (end2 - start2);
}

/**
 * Numeric bounds, inclusive on both ends.
 */
export interface Bounds {
  min: number;
  max: number;
}

export const Bounds = {
  minBounded(a: Bounds): Bounds {
    return { min: a.min, max: Math.max(a.min, a.max) };
  },
  contains(a: Bounds, b: Bounds) {
    return a.min <= b.min && a.max >= b.max;
  },
  containsValue(a: Bounds, value: number) {
    return a.min <= value && a.max >= value;
  },
  intersects(a: Bounds, b: Bounds): boolean {
    return a.min <= b.max && a.max >= b.min;
  },
  extendsWith(self: Bounds, other: Bounds) {
    self.min = Math.min(self.min, other.min);
    self.max = Math.max(self.max, other.max);
  },
  union(a: Bounds, b: Bounds) {
    return { min: Math.min(a.min, b.min), max: Math.max(a.max, b.max) };
  }
};

/**
 * Create a React context pair with type-safe provider and consumer hook.
 *
 * Replaces the Vue `makeProvider` pattern.
 *
 * @returns [Context, useHook] tuple
 */
export function makeContext<T>(label: string): [Context<T | null>, () => T] {
  const Ctx = createContext<T | null>(null);
  Ctx.displayName = label;

  function useInstance(): T {
    const instance = useContext(Ctx);
    if (instance == null) {
      throw new Error(label + ' not provided');
    }
    return instance;
  }

  return [Ctx, useInstance];
}

/**
 * Create an LRU cached variant of function.
 */
export function lruCached<P extends unknown[], V>(
  fn: (...args: P) => V,
  maxLength: number,
  keyFn: (...args: P) => string
): (...args: P) => V {
  const cache: Map<string, { value: V }> = new Map();
  let keyAtFront: string | null = null;

  return (...args: P): V => {
    const key = keyFn(...args);
    const fromCache = cache.get(key);
    if (fromCache !== undefined) {
      if (keyAtFront !== key) {
        cache.delete(key);
        cache.set(key, fromCache);
        keyAtFront = key;
      }
      return fromCache.value;
    } else {
      const value = fn(...args);
      cache.set(key, { value });
      if (cache.size > maxLength) {
        const deleteKey = cache.keys().next().value;
        if (deleteKey != null) {
          cache.delete(deleteKey);
          if (deleteKey === keyAtFront) {
            keyAtFront = null;
          }
        }
      }
      return value;
    }
  };
}

/**
 * A generator of unique labels with given prefix.
 */
export function labelGenerator(
  existing: readonly string[],
  prefix: string
): () => string {
  const existingLabels: Set<string> = new Set();
  existing.forEach((label) => existingLabels.add(label));

  let i = 0;
  return () => {
    for (;;) {
      const label = `${prefix}${i}`;
      i++;
      if (!existingLabels.has(label)) {
        return label;
      }
    }
  };
}
